import type { Appointment, Encounter, Patient } from '@medplum/fhirtypes';
import {
  applyLocationConfirmationToAppointment,
  applyLocationConfirmationToEncounter,
  buildLocationConfirmation,
  findLocationConfirmation,
  getClientLocationConfirmations,
} from '@unify/fhir-model/resources/client-location-confirmation';
import type {
  ClientLocationConfirmation,
  ClientLocationConfirmationSource,
  LocationConfirmationInput,
} from './types';
import { checkLicensureForSessionLocation } from './licensureCheck';

/**
 * Confirmations live on the appointment's Encounter (`Encounter.location`); the Appointment is passed for
 * appointments with no Encounter and older data, read for a client the Encounter has none for.
 */
export { getClientLocationConfirmations };

export function getClientLocationConfirmationForPatient(
  encounter: Encounter | null | undefined,
  appointment: Appointment | null | undefined,
  patientId: string | undefined,
): ClientLocationConfirmation | null {
  if (!patientId) return null;
  return findLocationConfirmation(encounter, appointment, { ref: `Patient/${patientId}` });
}

/** A Patient's confirmation (provider edits); joiners' own confirmations are written by the Chime bots. */
export function buildClientLocationConfirmation(
  appointment: Appointment,
  patientId: string,
  input: LocationConfirmationInput,
  opts?: { sourceOverride?: ClientLocationConfirmationSource },
): ClientLocationConfirmation {
  const practitionerId = appointment.participant
    ?.find((p) => p.actor?.reference?.startsWith('Practitioner/'))
    ?.actor?.reference?.split('/')[1];
  return buildLocationConfirmation(appointment, { ref: `Patient/${patientId}` }, input, {
    sourceOverride: opts?.sourceOverride,
    licensureBlocking: checkLicensureForSessionLocation({ attestedState: input.state, practitionerId }).blocking,
  });
}

/** Upserts one participant's confirmation on the Encounter; preserves other participants' entries. */
export const applyClientLocationConfirmationToEncounter = applyLocationConfirmationToEncounter;

/** The same, in the Appointment's private record, for an appointment with no Encounter. */
export const applyClientLocationConfirmationToAppointment = applyLocationConfirmationToAppointment;

export function patientHomeAddressPrefill(patient: Patient | undefined): {
  street: string;
  city: string;
  state: string;
  postalCode: string;
} | null {
  const home = patient?.address?.find((a) => a.use === 'home' || !a.use) ?? patient?.address?.[0];
  if (!home) return null;
  const street = home.line?.[0]?.trim() ?? '';
  const city = home.city?.trim() ?? '';
  const state = home.state?.trim()?.toUpperCase() ?? '';
  const postalCode = home.postalCode?.trim() ?? '';
  if (!street && !city && !state && !postalCode) return null;
  return { street, city, state, postalCode };
}

/** Single-line label for note attendance/location header and pickers. */
export function formatClientLocationConfirmationLabel(
  confirmation: Pick<ClientLocationConfirmation, 'street' | 'city' | 'state' | 'postalCode'>,
): string {
  const parts = [
    confirmation.street.trim(),
    confirmation.city.trim(),
    `${confirmation.state.trim()} ${confirmation.postalCode.trim()}`.trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

/** Map patient id → confirmed location label (the Encounter's, else older Appointment data). */
export function getClientLocationLabels(
  encounter: Encounter | null | undefined,
  appointment: Appointment | null | undefined,
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const confirmation of getClientLocationConfirmations(encounter, appointment)) {
    if (!confirmation.confirmedByPatientId) continue;
    labels[confirmation.confirmedByPatientId] = formatClientLocationConfirmationLabel(confirmation);
  }
  return labels;
}
